import { alpha, Theme, TypeAction, useTheme } from "@mui/material";
import { useMemo } from "react";
import { ChartColors } from "./customThemeTypes";

export const white = "#ffffff";
export const ultraLightGray = "#eeeeee";
export const lighterGray = "#dddddd";
export const lightGray = "#cccccc";
export const gray = "#999999";
export const darkGray = "#555555";
export const darkerGray = "#444444";
export const veryDarkGray = "#333333";
export const veryVeryDarkGray = "#222222";
export const ultraDarkGray = "#191919";
export const nearlyBlack = "#101010";
export const black = "#000000";

export const turquoiseAirmont = "#669999";
export const turquoise0 = "#101919";
export const turquoise1 = "#1F2E2E";
export const turquoise2 = "#334D4D";
export const turquoise3 = "#486A6A";
export const turquoise4 = "#669999"; //airmontTurquoise
export const turquoise5 = "#7AA6A6";
export const turquoise6 = "#8FB3B3";
export const turquoise7 = "#A3C2C2";
export const turquoise8 = "#B8D0D0";
export const turquoise9 = "#D1E3E3";
export const turquoise10 = "#E6F1F1";
export const turquoise11 = "#F7FAFA";
export const turquoiseShades = [
  turquoise1,
  turquoise2,
  turquoise3,
  turquoise4,
  turquoise5,
  turquoise6,
  turquoise7,
  turquoise8,
  turquoise9,
  turquoise10,
];

// UI orange palette (charts remain turquoise via separate constants)
export const uiOrangePrimary = "#FF7400"; // base orange

export const uiOrange1 = "#2A1A0A";
export const uiOrange2 = "#4A3016";
export const uiOrange3 = "#6A4724";
export const uiOrange4 = "#FF7400"; // base
export const uiOrange5 = "#FF8E33";
export const uiOrange6 = "#FFA24D";
export const uiOrange7 = "#FFB673";
export const uiOrange8 = "#FFC285";
export const uiOrange9 = "#FFCCA0";
export const uiOrange10 = "#FFE0C2";
export const uiOrange11 = "#FFF1E3";
export const uiOrangeShades = [
  uiOrange1,
  uiOrange2,
  uiOrange3,
  uiOrange4,
  uiOrange5,
  uiOrange6,
  uiOrange7,
  uiOrange8,
  uiOrange9,
  uiOrange10,
  uiOrange11,
];

// Rich tonal palette (Material-inspired)
export const red = "#D32F2F"; // rich red
export const darkOrange = "#E45618"; // midpoint between red and orange
export const lightOrange = "#F57C00"; // rich orange
export const darkYellow = "#F89E17"; // midpoint between orange and yellow
export const yellow = "#FBC02D"; // strong yellow
export const brightGreen = "#BCBA38"; // midpoint between yellow and lightGreen
export const lighterGreen = "#7CB342"; // light green
export const lightGreen = "#55983A"; // midpoint between lightGreen and green
export const green = "#2E7D32"; // rich green
export const redToGreen = [
  red,
  darkOrange,
  lightOrange,
  darkYellow,
  yellow,
  brightGreen,
  lighterGreen,
  lightGreen,
  green,
];

export const orange = "#FA854F";

export type ColorName = "primary" | "secondary" | "tertiary";

export type ActionGroupColorName =
  | "active"
  | "hover"
  | "selected"
  | "disabled"
  | "disabledBackground"
  | "focus";

export const getContainer = (theme: Theme, name: ColorName): string => {
  const palette = theme.palette;
  const colorPalette = palette[name];
  if (colorPalette?.container !== undefined) {
    return colorPalette.container;
  }
  const mainColor = colorPalette?.main ?? theme.palette.background.default;
  return alpha(mainColor, 0.6);
};

export const getPaletteActionGroup = (theme: Theme): TypeAction | undefined => {
  const palette = theme.palette;
  return palette.actionGroup;
};

export const getPaletteAction = (theme: Theme): TypeAction => {
  const palette = theme.palette;
  return palette.action;
};

export const getPaletteChart = (theme: Theme): ChartColors => {
  const palette = theme.palette;
  return palette.chart ?? {};
};

export const getActionGroupColor = (
  theme: Theme,
  color: ActionGroupColorName
): string => {
  const palette = theme.palette;
  const actionGroup = palette.actionGroup;
  if (actionGroup) {
    return actionGroup[color];
  }
  return palette.primary.main;
};

export const useContainerColor = (color: ColorName): string => {
  const theme = useTheme();
  //console.log(theme);
  return useMemo(() => {
    return getContainer(theme, color);
  }, [color, theme]);
};

export const useActionGroupColor = (color: ActionGroupColorName): string => {
  const theme = useTheme();
  //console.log(theme);
  return useMemo(() => {
    return getActionGroupColor(theme, color);
  }, [color, theme]);
};

export const useActionGroupColors = (): TypeAction | undefined => {
  const theme = useTheme();
  //console.log(theme);
  return useMemo(() => {
    return getPaletteActionGroup(theme);
  }, [theme]);
};

export const useActionColors = (): TypeAction => {
  const theme = useTheme();
  //console.log(theme);
  return useMemo(() => {
    return getPaletteAction(theme);
  }, [theme]);
};

export const useChartColors = (): ChartColors => {
  const theme = useTheme();
  //console.log(theme);
  return useMemo(() => {
    return getPaletteChart(theme);
  }, [theme]);
};

export const getOnContainer = (theme: Theme, name: ColorName): string => {
  const palette = theme.palette;
  const colorPalette = palette[name];
  if (colorPalette?.onContainer !== undefined) {
    return colorPalette.onContainer;
  }
  //const containerColor = getContainer(theme, name);
  return theme.palette.text.primary;
};

export const useOnContainerColor = (color: ColorName): string => {
  const theme = useTheme();
  //console.log(theme);
  return useMemo(() => {
    return getOnContainer(theme, color);
  }, [color, theme]);
};
